import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentUserId } from '../../../features/auth/authSlice';
import { selectEvents, selectSwarms, setEvents, setSwarms, setUserBuys, updateBeeSwarmSoftClosing } from '../store';
import { useLazyGetUserBuysQuery, useLazyGetEventsQuery } from '../../../services/eventApi';

/**
 * Interface defining the SwarmBuyRequest object structure
 */
export interface SwarmBuyRequest {
  bteId: number;
  buyManagerId: number;
  reward: number;
  id: number;
  swarmId: number;
  description: string;
  swarmResolutionTypeId: number;
  reasonId: number | null;
  quantity: number;
  section: string;
  row: string;
  seats: string;
  totalCost: number;
  deliveryMethodID: number;
  createdOn: string;
  createdBy: number;
  acknowledge: boolean;
  userId: number;
  swarmRewardTypeId: number;
  rewardAmout: number;
  isLimitedView: boolean;
  isNonTransferrableLanguage: boolean;
  modifiedOn: string;
  modifiedBy: number;
}

/**
 * Interface for edit swarm event
 */
export interface EditSwarmEvent {
  completed: number;
  accepted: number;
  pending: number;
  id: number;
  bteId: number;
  instructions: string;
  rewardTypeId: number;
  strategyTypeId: number;
  running: boolean;
  createdOn: string;
  createdBy: number;
  salesDate: string;
  reward: number;
  isMonitor: boolean;
  isTraining: boolean;
  isNow: boolean;
  code: string;
  modifiedOn: string;
  modifiedBy: number;
  flag: boolean;
  buyManagerId: number;
  alternativeEventUrl: string;
  isApproveAllActive: boolean;
  buyRequests: any[];
}

/**
 * Interface for update event
 */
export interface UpdateEvent {
  bteId: number;
  exchangeId: number;
  name: string;
  date: string;
  primaryEventUrl: string | null;
  performers: {
    id: number;
    name: string;
  }[];
  venue: {
    id: number;
    name: string;
    timezoneName: string;
    streetAddress1: string;
    streetAddress2: string;
    city: string;
    state: string;
  };
  eventLimitNumberOfTickets: number;
  eventLimit1PurchasePerBuyer: boolean;
  runLimit1PurchasePerBuyer: boolean;
  runLimitNumberOfTickets: number | null;
  useRunLevelLimit: boolean;
  runId: number | null;
  runEventIds: number[];
  salesDates: any[];
  entityLink: string | null;
}

/**
 * Hook to handle SignalR events for Swarm Buy Requests
 * @param signalRConnection - The SignalR connection object
 */
/**
 * Hook to handle SignalR events for Swarm Buy Requests
 * @param signalRConnection - The SignalR connection object
 */

export const useSwarmsForBeeSignalR = (signalRConnection: any): void => {
  // Get the current userId from Redux
  const userId = useSelector(selectCurrentUserId);
  const dispatch = useDispatch();
  
  // Get current swarms and events from the store
  const swarms = useSelector(selectSwarms);
  const events = useSelector(selectEvents);
  
  // Get the lazy query functions
  const [getUserBuys] = useLazyGetUserBuysQuery();
  const [getEventsQuery] = useLazyGetEventsQuery();

  // Helper function to update an existing swarm
  const updateExistingSwarm = (existingIndex: number, swarmData: EditSwarmEvent): void => {
    console.log("[updateExistingSwarm] Updating existing swarm");
    
    // Create a new array with the updated swarm
    let swarmsCopy = [...swarms];
    
    // Dispatch the update action
    if(swarmsCopy[existingIndex].running && !swarmData.running){ // swarm has been closed
      // used to notify the bee
      dispatch(updateBeeSwarmSoftClosing(swarmData));
    }else{
      let updatedSwarms = swarmsCopy;
      updatedSwarms[existingIndex] = swarmData;
      dispatch(setSwarms(updatedSwarms));
    }
  };

  // Helper function to check if event exists and fetch it if needed
  const ensureEventExists = async (bteId: number): Promise<boolean> => {
    if (!events) return false;

    const eventExists = events.some((e: any) => e.bteId === bteId);
    
    if (!eventExists && bteId) {
      // If the event doesn't exist but we have bteId, fetch it
      console.log("[ensureEventExists] Event information not found, fetching it first");
      
      try {
        const query = `?bteId=${bteId}`;
        const result = await getEventsQuery(query).unwrap();
        
        if (result && result.length > 0) {
          console.log("[ensureEventExists] Fetched event information:", result[0].eventModel);
          
          // Add the event to store
          const updatedEvents = [...events, result[0].eventModel];
          dispatch(setEvents(updatedEvents));
          return true;
        }
      } catch (error) {
        console.error("[ensureEventExists] Error fetching event information:", error);
        // Continue even if event fetch fails
        return false;
      }
    }
    
    return eventExists;
  };

  // Helper function to add a new swarm to the store
  const addNewSwarm = (swarmData: EditSwarmEvent): void => {
    console.log("[addNewSwarm] Adding new swarm to store");
    
    // Create a new array with the new swarm added
    const updatedSwarms = [...swarms, swarmData];
    
    // Dispatch the update action
    dispatch(setSwarms(updatedSwarms));
  };

  // Helper function to update an existing event
  const updateExistingEvent = (existingIndex: number, eventData: UpdateEvent): void => {
    console.log("[updateExistingEvent] Updating existing event");
    
    // Create a new array with the updated event
    const updatedEvents = [...events];
    updatedEvents[existingIndex] = eventData;
    
    // Dispatch the update action
    dispatch(setEvents(updatedEvents));
  };

  // Helper function to add a new event to the store
  const addNewEvent = (eventData: UpdateEvent): void => {
    console.log("[addNewEvent] Adding new event to store");
    
    // Create a new array with the new event added
    const updatedEvents = [...events, eventData];
    
    // Dispatch the update action
    dispatch(setEvents(updatedEvents));
  };

  // Helper function to refresh user buys
  const refreshUserBuys = (): void => {
    console.log("[refreshUserBuys] Getting updated user buys");
    
    // Call the API to get updated user buys
    getUserBuys(undefined)
      .then(response => {
        if (response.data) {
          console.log("[refreshUserBuys] Updated UserBuys: ", response.data);
          dispatch(setUserBuys(response.data));
        }
      })
      .catch(error => {
        console.error("[refreshUserBuys] Error fetching UserBuys:", error);
      });
  };

  useEffect(() => {
    if (!signalRConnection) return;

    /**
     * Process a swarm edit event from SignalR.
     * Updates existing swarm or adds new one if running.
     * @param swarmEvent Swarm data received from the event
     */
    const handleEditSwarmEvent = async (swarmEvent: EditSwarmEvent): Promise<void> => {
      console.log("[handleEditSwarmEvent] event: ", swarmEvent);
      
      if (!swarms) {
        console.log("[handleEditSwarmEvent] No swarms in store");
        return;
      }
      
      // Check if the swarm already exists in the store
      const existingSwarmIndex = swarms.findIndex((s: EditSwarmEvent) => s.id === swarmEvent.id);
      
      if (existingSwarmIndex !== -1) {
        // If the swarm already exists, update it
        updateExistingSwarm(existingSwarmIndex, swarmEvent);
      } else {
        // If it doesn't exist, check if we should add it
        if (swarmEvent.running) {
          console.log("[handleEditSwarmEvent] New running swarm, adding to store");
          
          // Before adding, ensure we have the event information
          if (swarmEvent.bteId) {
            await ensureEventExists(swarmEvent.bteId);
          }
          
          // Add the new swarm
          addNewSwarm(swarmEvent);
        } else {
          console.log("[handleEditSwarmEvent] New swarm is not running, skipping");
        }
      }
    };

    /**
     * Process an update event from SignalR.
     * Updates venue and performers info for an event.
     * @param updateEvent Event information with venue and performers
     */
    const handleUpdateEvent = (updateEvent: UpdateEvent): void => {
      console.log("[handleUpdateEvent] event: ", updateEvent);
      
      if (!events) {
        console.log("[handleUpdateEvent] No events in store");
        return;
      }
      
      // Check if the event already exists in the store
      const existingEventIndex = events.findIndex((e: UpdateEvent) => e.bteId === updateEvent.bteId);
      
      if (existingEventIndex !== -1) {
        // If it exists, update it
        updateExistingEvent(existingEventIndex, updateEvent);
      } else {
        // If it doesn't exist, add it
        addNewEvent(updateEvent);
      }
    };

    /**
     * Process a new swarm purchase event from SignalR.
     * Refreshes user buys if the purchase is for the current user.
     * @param req Swarm buy request data
     */
    const handleNewSwarmPurchaseEvent = (req: SwarmBuyRequest): void => {
      console.log("[handleNewSwarmPurchaseEvent] req: ", req);
      
      // Only process if the request is for the current user
      if (req.userId == userId) {
        console.log("[handleNewSwarmPurchaseEvent] signalR event received for current user");
        refreshUserBuys();
      }
    };


    // Event handlers mapping
    const eventHandlers: Record<string, (data: any) => void> = {
      'EditSwarmEvent': handleEditSwarmEvent,
      'UpdateEvent': handleUpdateEvent,
      'NewSwarmPurchase': handleNewSwarmPurchaseEvent
    };
    
    // Attach handlers
    Object.entries(eventHandlers).forEach(([event, handler]) => {
      signalRConnection.on(event, handler);
    });
    
    // Cleanup
    return () => {
      if (signalRConnection) {
        Object.entries(eventHandlers).forEach(([event, handler]) => {
          signalRConnection.off(event, handler);
        });
      }
    };
  }, [signalRConnection, userId, swarms, events, getEventsQuery, getUserBuys, dispatch]);
};
